import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import * as Sentry from '@sentry/react';
import { apiKeyRotateGracePeriod, apiKeyRotateGracePeriodLabel } from '../../../constants/enum';
import {
  apiKeyBoxBg,
  gray40,
  gray80,
  primaryButtonBackgroundColor,
  red20,
  red50,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { dialogText, dialogTitle, regularXs } from '../../../constants/location';
import type { ApiKeyListItem, ApiKeyRotatedResponse } from '../../../types/apiKey';
import { rotateApiKey } from '../../../api/firebase/function/apiKeyFunction';
import CustomSelectField from '../../uiParts/form/CustomSelectField';

interface Props {
  open: boolean;
  target: ApiKeyListItem | null;
  companyId?: string;
  onClose: () => void;
  onRotated: () => void;
}

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '480px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  dialogTitle,
  fieldWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  targetText: {
    ...dialogText,
  },
  helperText: {
    ...regularXs,
    color: gray80,
  },
  warningText: {
    ...dialogText,
    color: red50,
    marginBottom: '16px',
  },
  keyBox: {
    alignItems: 'center',
    backgroundColor: apiKeyBoxBg,
    borderRadius: '4px',
    display: 'flex',
    fontFamily: 'monospace',
    gap: '8px',
    padding: '12px 16px',
    wordBreak: 'break-all' as const,
  },
  keyBoxText: {
    flex: 1,
    fontSize: '14px',
  },
  errorBox: {
    backgroundColor: red20,
    border: `1px solid ${red50}`,
    borderRadius: '4px',
    color: red50,
    fontSize: '14px',
    padding: '8px 12px',
    marginTop: '8px',
  },
  dialogActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '32px',
  },
  actionButton: {
    height: '44px',
    minWidth: '120px',
  },
};

const ApiKeyRotateDialog: React.FC<Props> = ({ open, target, companyId, onClose, onRotated }) => {
  const [grace, setGrace] = useState<number>(apiKeyRotateGracePeriod.oneHour);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [rotated, setRotated] = useState<ApiKeyRotatedResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const reset = (): void => {
    setGrace(apiKeyRotateGracePeriod.oneHour);
    setErrorMessage('');
    setRotated(null);
    setCopied(false);
  };

  const handleClose = (): void => {
    if (submitting) return;
    if (rotated) {
      onRotated();
    }
    reset();
    onClose();
  };

  const handleSubmit = async (): Promise<void> => {
    if (!target) return;
    setSubmitting(true);
    setErrorMessage('');
    try {
      const result = await rotateApiKey({
        company_id: companyId,
        api_key_id: target.api_key_id,
        grace_period_minutes: grace,
      });
      setRotated(result);
    } catch (e) {
      const message = e instanceof Error ? e.message : '再生成に失敗しました。';
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async (): Promise<void> => {
    if (!rotated) return;
    try {
      await navigator.clipboard.writeText(rotated.plaintext_key);
      setCopied(true);
      setErrorMessage('');
    } catch (e) {
      setCopied(false);
      Sentry.captureException(e);
      setErrorMessage(
        'クリップボードへのコピーに失敗しました。手動でキーを選択してコピーしてください。',
      );
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} sx={styles.dialog}>
      <Box sx={styles.dialogHeader}>
        <Typography sx={styles.dialogTitle}>
          {rotated ? 'APIキーを再生成しました' : 'APIキーを再生成'}
        </Typography>
        <IconButton onClick={handleClose} disabled={submitting} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {rotated ? (
        <Box>
          <Typography sx={styles.warningText}>
            新しいキーは <strong>このダイアログを閉じると二度と表示されません</strong>。旧キーは{' '}
            {new Date(rotated.rotated_expires_at).toLocaleString()} 以降に失効します。
          </Typography>
          <Box sx={styles.keyBox}>
            <Box sx={styles.keyBoxText}>{rotated.plaintext_key}</Box>
            <Tooltip title={copied ? 'コピー済み' : 'コピー'}>
              <IconButton onClick={handleCopy} aria-label="APIキーをコピー" size="small">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {errorMessage && <Box sx={styles.errorBox}>{errorMessage}</Box>}
          <Box sx={styles.dialogActions}>
            <Button onClick={handleClose} sx={[primaryButtonBackgroundColor, styles.actionButton]}>
              閉じる
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={styles.fieldWrapper}>
          <Typography sx={styles.targetText}>
            対象キー: <strong>{target?.display_name}</strong>（末尾 {target?.key_last4}）
          </Typography>
          <CustomSelectField
            labelText="旧キーの失効タイミング"
            value={grace}
            onChange={(e): void => setGrace(Number(e.target.value))}
            list={Object.entries(apiKeyRotateGracePeriodLabel).map(([minutes, label]) => ({
              value: Number(minutes),
              option: label,
            }))}
            formMb="0"
          />
          <Typography sx={styles.helperText}>
            「即時失効」を選ぶと旧キーは直ちに 401 を返します。本番運用では 1〜24
            時間の猶予を選択し、切替後に旧キーを無効化することを推奨します。
          </Typography>
          {errorMessage && <Box sx={styles.errorBox}>{errorMessage}</Box>}
          <Box sx={styles.dialogActions}>
            <Button
              onClick={handleClose}
              disabled={submitting}
              sx={[tertiaryButtonBackgroundColor, styles.actionButton]}
            >
              キャンセル
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={[primaryButtonBackgroundColor, styles.actionButton]}
            >
              再生成する
            </Button>
          </Box>
        </Box>
      )}
    </Dialog>
  );
};

export default ApiKeyRotateDialog;
